import { useDeferredValue, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api/useApi';
import { Button } from '@/shared/components';
import { PluginProblem } from './PluginProblem';

export function PluginPropertyPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const deferred = useDeferredValue(search);
  const query = useQuery({
    queryKey: ['plugin-properties', deferred, page],
    queryFn: () => api.properties.list({ search: deferred, page, pageSize: 25, lifecycle: 'All' }),
    retry: false,
  });
  return (
    <fieldset className="border-line space-y-3 rounded-md border p-4">
      <legend className="px-1 text-sm font-semibold">
        Apartments auswählen · {value.length} ausgewählt
      </legend>
      <label className="block text-sm">
        Apartment suchen
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="border-line bg-card mt-1 min-h-11 w-full rounded-md border px-3"
        />
      </label>
      <PluginProblem error={query.error} />
      {query.isError && (
        <Button type="button" size="lg" onClick={() => void query.refetch()}>
          Erneut laden
        </Button>
      )}
      {query.isPending && <p role="status">Apartments werden geladen…</p>}
      {query.data?.items.map((property) => (
        <label
          key={property.id}
          className="flex min-h-11 cursor-pointer items-center gap-3 text-sm"
        >
          <input
            type="checkbox"
            checked={value.includes(property.id)}
            className="accent-sage h-5 w-5 shrink-0"
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? [...value, property.id]
                  : value.filter((id) => id !== property.id),
              )
            }
          />
          <span className="min-w-0 break-words">{property.name}</span>
        </label>
      ))}
      {query.data?.total === 0 && (
        <p className="text-ink-soft text-sm">Keine passenden Apartments.</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="lg" disabled={page === 1} onClick={() => setPage(page - 1)}>
          Zurück
        </Button>
        <span className="text-sm">Seite {page}</span>
        <Button
          type="button"
          size="lg"
          disabled={!query.data || page * 25 >= query.data.total}
          onClick={() => setPage(page + 1)}
        >
          Weiter
        </Button>
      </div>
    </fieldset>
  );
}
